import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Stack,
} from "@mui/material";
import { toast } from "sonner";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import AccountService from "../../../../services/AccountServices";
import "../../../common/commonStyles.css";

const SageCustomerMemosPopup = ({ open, onClose, customer }) => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const authToken = token || accessToken;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [memoText, setMemoText] = useState("");

  const customerId = customer?.id || "";
  const accountNo = String(
    customer?.accound_no ||
      customer?.account_no ||
      customer?.accountNo ||
      customer?.account_number ||
      ""
  ).trim();
  const customerName = customer?.company || customer?.name || accountNo || "Customer";

  const loadMemos = useCallback(() => {
    if (!customerId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    AccountService.fetchSageCustomerMemos(
      {
        company_id: customerId,
        customer_id: customerId,
        page: 1,
        per_page: 50,
      },
      authToken
    )
      .then((payload) => {
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.memos)
            ? payload.memos
            : Array.isArray(payload)
              ? payload
              : [];
        setRows(list);
      })
      .catch(() => {
        setRows([]);
        setError("Could not load Sage memos");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [customerId, authToken]);

  useEffect(() => {
    if (!open || !customerId) {
      setRows([]);
      setError("");
      setMemoText("");
      return;
    }
    loadMemos();
  }, [open, customerId, loadMemos]);

  const handleAddMemo = () => {
    const memo = memoText.trim();
    if (!memo) {
      toast.error("Enter memo text");
      return;
    }
    if (!customerId) {
      toast.error("Select a customer");
      return;
    }
    setSaving(true);
    AccountService.addSageCustomerMemo(
      {
        company_id: customerId,
        customer_id: customerId,
        memo,
      },
      authToken
    )
      .then((payload) => {
        if (payload && payload.success === false) {
          toast.error(payload.message || "Failed to add memo");
          return;
        }
        toast.success(payload?.message || "Memo added");
        setMemoText("");
        loadMemos();
      })
      .catch(() => {
        toast.error("Failed to add memo");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Memos linked in Sage - {customerName}
        {accountNo ? ` (${accountNo})` : ""}
        <IconButton onClick={onClose} size="small">
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
          <TextField
            className="input-border"
            fullWidth
            size="small"
            label="Memo"
            placeholder="Enter memo"
            value={memoText}
            onChange={(e) => setMemoText(e.target.value)}
            multiline
            minRows={1}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#000",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#000",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#000",
              },
            }}
          />
          <Button
            variant="contained"
            className="button-variant-contained"
            startIcon={<Iconify icon="eva:plus-fill" width={16} />}
            onClick={handleAddMemo}
            disabled={saving || !customerId}
            sx={{ whiteSpace: "nowrap", minWidth: 110 }}
          >
            Add Memo
          </Button>
        </Stack>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Memo</TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && error && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="error" align="center">
                      {error}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No Sage memos found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                !error &&
                rows.map((row, index) => (
                  <TableRow key={row.id || row.memo_id || index} hover>
                    <TableCell>
                      {row.memo_date_display ||
                        row.memo_date ||
                        row.date_display ||
                        row.date ||
                        row.created_at ||
                        "-"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      {row.memo ||
                        row.memo_text ||
                        row.note ||
                        row.comment ||
                        row.description ||
                        "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 90,
                        maxWidth: 90,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {row.user_name || row.user || row.created_by || "-"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SageCustomerMemosPopup;
